import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { reportService } from '../../services/reportService';

const AdminReports = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('generate');

  // Report generation state
  const [reportName, setReportName] = useState('Report Performance');
  const [dateRangeType, setDateRangeType] = useState('last_30_days');
  const [customStartDate, setCustomStartDate] = useState(format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'));
  const [customEndDate, setCustomEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedMetrics, setSelectedMetrics] = useState([
    'total_stores',
    'total_views',
    'total_saves',
    'total_promotions',
    'total_engagement',
    'avg_views_per_store',
    'avg_saves_per_store',
    'category_performance',
    'store_performance'
  ]);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Template management state
  const [templates, setTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    templateName: '',
    description: '',
    isDefault: false
  });
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Report history state
  const [reportHistory, setReportHistory] = useState([]);

  const availableMetrics = reportService?.getAvailableMetrics();
  const dateRangePresets = reportService?.getDateRangePresets();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userRole !== 'admin') {
      navigate('/');
      return;
    }

    loadTemplates();
    loadReportHistory();
  }, [user, userRole, navigate]);

  const loadTemplates = async () => {
    try {
      const data = await reportService?.getTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  const loadReportHistory = async () => {
    try {
      const data = await reportService?.getReportHistory(20);
      setReportHistory(data);
    } catch (err) {
      console.error('Error loading report history:', err);
    }
  };

  const handleMetricToggle = (metricValue) => {
    setSelectedMetrics(prev => {
      if (prev?.includes(metricValue)) {
        return prev?.filter(m => m !== metricValue);
      } else {
        return [...prev, metricValue];
      }
    });
  };

  const handleSelectAllMetrics = () => {
    if (selectedMetrics?.length === availableMetrics?.length) {
      setSelectedMetrics([]);
    } else {
      setSelectedMetrics(availableMetrics?.map(m => m?.value));
    }
  };

  const getDateRange = () => {
    if (dateRangeType === 'custom') {
      return {
        start: new Date(customStartDate),
        end: new Date(customEndDate)
      };
    }

    const preset = dateRangePresets?.find(p => p?.value === dateRangeType);
    return preset?.getRange();
  };

  const handleGenerateReport = async () => {
    if (selectedMetrics?.length === 0) {
      setError('Seleziona almeno una metrica');
      return;
    }

    try {
      setGenerating(true);
      setError('');
      const dateRange = getDateRange();
      
      const data = await reportService?.generateReportData(
        dateRange?.start?.toISOString(),
        dateRange?.end?.toISOString(),
        selectedMetrics
      );

      setGeneratedReport({
        name: reportName,
        data,
        dateRange,
        metrics: selectedMetrics
      });
      setSuccess('Report generato con successo');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Errore nella generazione del report');
    } finally {
      setGenerating(false);
    }
  };

  const handleExportCSV = async () => {
    if (!generatedReport) return;

    try {
      reportService?.exportToCSV(
        generatedReport?.data,
        generatedReport?.name,
        generatedReport?.dateRange?.start,
        generatedReport?.dateRange?.end
      );

      await reportService?.saveReportHistory({
        reportName: generatedReport?.name,
        metrics: generatedReport?.metrics,
        dateRangeStart: generatedReport?.dateRange?.start?.toISOString(),
        dateRangeEnd: generatedReport?.dateRange?.end?.toISOString(),
        fileFormat: 'csv'
      });

      setSuccess('Report CSV esportato con successo');
      setTimeout(() => setSuccess(''), 3000);
      loadReportHistory();
    } catch (err) {
      setError(err?.message || 'Errore nell\'esportazione CSV');
    }
  };

  const handleExportPDF = async () => {
    if (!generatedReport) return;

    try {
      reportService?.exportToPDF(
        generatedReport?.data,
        generatedReport?.name,
        generatedReport?.dateRange?.start,
        generatedReport?.dateRange?.end
      );

      await reportService?.saveReportHistory({
        reportName: generatedReport?.name,
        metrics: generatedReport?.metrics,
        dateRangeStart: generatedReport?.dateRange?.start?.toISOString(),
        dateRangeEnd: generatedReport?.dateRange?.end?.toISOString(),
        fileFormat: 'pdf'
      });

      setSuccess('Report PDF esportato con successo');
      setTimeout(() => setSuccess(''), 3000);
      loadReportHistory();
    } catch (err) {
      setError(err?.message || 'Errore nell\'esportazione PDF');
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateForm?.templateName?.trim()) {
      setError('Inserisci un nome per il template');
      return;
    }

    try {
      setLoading(true);
      setError('');

      if (editingTemplate) {
        await reportService?.updateTemplate(editingTemplate?.id, {
          templateName: templateForm?.templateName,
          description: templateForm?.description,
          metrics: selectedMetrics,
          dateRangeType,
          isDefault: templateForm?.isDefault
        });
        setSuccess('Template aggiornato con successo');
      } else {
        await reportService?.createTemplate({
          templateName: templateForm?.templateName,
          description: templateForm?.description,
          metrics: selectedMetrics,
          dateRangeType,
          isDefault: templateForm?.isDefault
        });
        setSuccess('Template salvato con successo');
      }

      setTimeout(() => setSuccess(''), 3000);
      setShowTemplateModal(false);
      setTemplateForm({ templateName: '', description: '', isDefault: false });
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) {
      setError(err?.message || 'Errore nel salvataggio del template');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadTemplate = (template) => {
    setReportName(template?.template_name);
    setSelectedMetrics(template?.metrics || []);
    setDateRangeType(template?.date_range_type);
    setSuccess(`Template "${template?.template_name}" caricato`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      templateName: template?.template_name,
      description: template?.description || '',
      isDefault: template?.is_default
    });
    setSelectedMetrics(template?.metrics || []);
    setDateRangeType(template?.date_range_type);
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Sei sicuro di voler eliminare questo template?')) return;

    try {
      await reportService?.deleteTemplate(templateId);
      setSuccess('Template eliminato con successo');
      setTimeout(() => setSuccess(''), 3000);
      loadTemplates();
    } catch (err) {
      setError(err?.message || 'Errore nell\'eliminazione del template');
    }
  };

  const getMetricsByCategory = (category) => {
    return availableMetrics?.filter(m => m?.category === category);
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Report Performance - Admin | CLICCAMI.NET</title>
      </Helmet>
      <Header onFilterClick={() => {}} />
      <div className="pt-24 pb-12 px-6">
        <div className="max-w-screen-2xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold text-foreground mb-2">Report Performance</h1>
              <p className="text-muted-foreground">Genera ed esporta report personalizzati con metriche selezionate</p>
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-foreground mb-1">Errore</p>
                  <p className="text-sm text-muted-foreground">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                <p className="text-sm text-foreground">{success}</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-border">
            <button
              onClick={() => setActiveTab('generate')}
              className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
                activeTab === 'generate' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="FileText" size={18} className="inline mr-2" />
              Genera Report
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
                activeTab === 'templates' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="Bookmark" size={18} className="inline mr-2" />
              Template ({templates?.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`px-6 py-3 font-semibold transition-colors border-b-2 ${
                activeTab === 'history' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name="History" size={18} className="inline mr-2" />
              Storico
            </button>
          </div>

          {/* Generate Report Tab */}
          {activeTab === 'generate' && (
            <div className="space-y-6">
              {/* Report Configuration */}
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <h2 className="text-2xl font-bold text-foreground mb-6">Configurazione Report</h2>
                
                <div className="space-y-6">
                  {/* Report Name */}
                  <Input
                    label="Nome Report"
                    value={reportName}
                    onChange={(e) => setReportName(e?.target?.value)}
                    placeholder="Es. Report Mensile Gennaio 2026"
                  />

                  {/* Date Range */}
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Periodo</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      {dateRangePresets?.map(preset => (
                        <button
                          key={preset?.value}
                          onClick={() => setDateRangeType(preset?.value)}
                          className={`px-4 py-2 rounded-lg border transition-colors ${
                            dateRangeType === preset?.value
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background border-border hover:border-primary'
                          }`}
                        >
                          {preset?.label}
                        </button>
                      ))}
                    </div>

                    {dateRangeType === 'custom' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                          type="date"
                          label="Data Inizio"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e?.target?.value)}
                        />
                        <Input
                          type="date"
                          label="Data Fine"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e?.target?.value)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Metrics Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="text-sm font-medium text-foreground">Metriche da Includere</label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAllMetrics}
                      >
                        {selectedMetrics?.length === availableMetrics?.length ? 'Deseleziona Tutto' : 'Seleziona Tutto'}
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {/* General Metrics */}
                      <div>
                        <h3 className="text-sm font-semibold text-foreground mb-2">Metriche Generali</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {getMetricsByCategory('general')?.map(metric => (
                            <label
                              key={metric?.value}
                              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedMetrics?.includes(metric?.value)}
                                onChange={() => handleMetricToggle(metric?.value)}
                                className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                              />
                              <span className="text-sm text-foreground">{metric?.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Engagement Metrics */}
                      <div>
                        <h3 className="text-sm font-semibold text-foreground mb-2">Engagement</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {getMetricsByCategory('engagement')?.map(metric => (
                            <label
                              key={metric?.value}
                              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedMetrics?.includes(metric?.value)}
                                onChange={() => handleMetricToggle(metric?.value)}
                                className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                              />
                              <span className="text-sm text-foreground">{metric?.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Promotion Metrics */}
                      <div>
                        <h3 className="text-sm font-semibold text-foreground mb-2">Promozioni</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {getMetricsByCategory('promotions')?.map(metric => (
                            <label
                              key={metric?.value}
                              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedMetrics?.includes(metric?.value)}
                                onChange={() => handleMetricToggle(metric?.value)}
                                className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                              />
                              <span className="text-sm text-foreground">{metric?.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Detailed Metrics */}
                      <div>
                        <h3 className="text-sm font-semibold text-foreground mb-2">Analisi Dettagliate</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {getMetricsByCategory('detailed')?.map(metric => (
                            <label
                              key={metric?.value}
                              className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedMetrics?.includes(metric?.value)}
                                onChange={() => handleMetricToggle(metric?.value)}
                                className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                              />
                              <span className="text-sm text-foreground">{metric?.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      variant="default"
                      iconName="BarChart3"
                      onClick={handleGenerateReport}
                      loading={generating}
                      disabled={selectedMetrics?.length === 0}
                    >
                      Genera Report
                    </Button>
                    <Button
                      variant="outline"
                      iconName="Save"
                      onClick={() => {
                        setEditingTemplate(null);
                        setTemplateForm({ templateName: reportName, description: '', isDefault: false });
                        setShowTemplateModal(true);
                      }}
                    >
                      Salva come Template
                    </Button>
                  </div>
                </div>
              </div>

              {/* Generated Report Preview */}
              {generatedReport && (
                <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-foreground mb-1">{generatedReport?.name}</h2>
                      <p className="text-sm text-muted-foreground">
                        Periodo: {format(generatedReport?.dateRange?.start, 'dd/MM/yyyy', { locale: it })} - {format(generatedReport?.dateRange?.end, 'dd/MM/yyyy', { locale: it })}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        iconName="FileText"
                        onClick={handleExportCSV}
                      >
                        Esporta CSV
                      </Button>
                      <Button
                        variant="default"
                        iconName="FileDown"
                        onClick={handleExportPDF}
                      >
                        Esporta PDF
                      </Button>
                    </div>
                  </div>

                  {/* Summary Metrics */}
                  {(generatedReport?.data?.total_stores !== undefined || 
                    generatedReport?.data?.total_views !== undefined) && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                      {generatedReport?.data?.total_stores !== undefined && (
                        <div className="bg-background rounded-lg p-4 border border-border">
                          <p className="text-sm text-muted-foreground mb-1">Vetrine Totali</p>
                          <p className="text-2xl font-bold text-foreground">{generatedReport?.data?.total_stores}</p>
                        </div>
                      )}
                      {generatedReport?.data?.total_views !== undefined && (
                        <div className="bg-background rounded-lg p-4 border border-border">
                          <p className="text-sm text-muted-foreground mb-1">Visualizzazioni</p>
                          <p className="text-2xl font-bold text-foreground">{generatedReport?.data?.total_views}</p>
                        </div>
                      )}
                      {generatedReport?.data?.total_saves !== undefined && (
                        <div className="bg-background rounded-lg p-4 border border-border">
                          <p className="text-sm text-muted-foreground mb-1">Salvataggi</p>
                          <p className="text-2xl font-bold text-foreground">{generatedReport?.data?.total_saves}</p>
                        </div>
                      )}
                      {generatedReport?.data?.total_engagement !== undefined && (
                        <div className="bg-background rounded-lg p-4 border border-border">
                          <p className="text-sm text-muted-foreground mb-1">Engagement</p>
                          <p className="text-2xl font-bold text-foreground">{generatedReport?.data?.total_engagement}</p>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-sm text-muted-foreground">
                    Report generato con {selectedMetrics?.length} metriche. Usa i pulsanti sopra per esportare in CSV o PDF.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Template Salvati</h2>
                  <Button
                    variant="default"
                    iconName="Plus"
                    onClick={() => {
                      setEditingTemplate(null);
                      setTemplateForm({ templateName: '', description: '', isDefault: false });
                      setShowTemplateModal(true);
                    }}
                  >
                    Nuovo Template
                  </Button>
                </div>

                {templates?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="Bookmark" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">Nessun template salvato</p>
                    <Button
                      variant="outline"
                      iconName="Plus"
                      onClick={() => setShowTemplateModal(true)}
                    >
                      Crea il tuo primo template
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates?.map(template => (
                      <div
                        key={template?.id}
                        className="bg-background rounded-lg p-4 border border-border hover:border-primary transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground mb-1">{template?.template_name}</h3>
                            {template?.description && (
                              <p className="text-sm text-muted-foreground mb-2">{template?.description}</p>
                            )}
                            {template?.is_default && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                <Icon name="Star" size={12} />
                                Default
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground mb-3">
                          <p>{template?.metrics?.length} metriche</p>
                          <p>Periodo: {dateRangePresets?.find(p => p?.value === template?.date_range_type)?.label}</p>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="Play"
                            onClick={() => {
                              handleLoadTemplate(template);
                              setActiveTab('generate');
                            }}
                            fullWidth
                          >
                            Usa
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Edit"
                            onClick={() => handleEditTemplate(template)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => handleDeleteTemplate(template?.id)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm">
                <h2 className="text-2xl font-bold text-foreground mb-6">Storico Report Generati</h2>

                {reportHistory?.length === 0 ? (
                  <div className="text-center py-12">
                    <Icon name="History" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Nessun report generato</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reportHistory?.map(report => (
                      <div
                        key={report?.id}
                        className="bg-background rounded-lg p-4 border border-border hover:border-primary transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground mb-1">{report?.report_name}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Icon name="Calendar" size={14} />
                                {format(new Date(report?.date_range_start), 'dd/MM/yyyy', { locale: it })} - {format(new Date(report?.date_range_end), 'dd/MM/yyyy', { locale: it })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Icon name="Clock" size={14} />
                                {format(new Date(report?.generated_at), 'dd/MM/yyyy HH:mm', { locale: it })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Icon name="FileText" size={14} />
                                {report?.file_format?.toUpperCase()}
                              </span>
                              <span className="text-xs">
                                {report?.metrics?.length} metriche
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl p-6 max-w-md w-full border border-border shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">
                {editingTemplate ? 'Modifica Template' : 'Nuovo Template'}
              </h3>
              <button
                onClick={() => {
                  setShowTemplateModal(false);
                  setEditingTemplate(null);
                  setTemplateForm({ templateName: '', description: '', isDefault: false });
                }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Nome Template"
                value={templateForm?.templateName}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, templateName: e?.target?.value }))}
                placeholder="Es. Report Settimanale"
                required
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Descrizione</label>
                <textarea
                  value={templateForm?.description}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e?.target?.value }))}
                  placeholder="Descrizione opzionale del template"
                  className="w-full h-24 px-3 py-2 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={templateForm?.isDefault}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, isDefault: e?.target?.checked }))}
                  className="w-4 h-4 rounded border-input text-primary focus:ring-2 focus:ring-primary"
                />
                <span className="text-sm text-foreground">Imposta come template predefinito</span>
              </label>

              <div className="text-sm text-muted-foreground">
                <p>Configurazione corrente:</p>
                <ul className="list-disc list-inside mt-2">
                  <li>{selectedMetrics?.length} metriche selezionate</li>
                  <li>Periodo: {dateRangePresets?.find(p => p?.value === dateRangeType)?.label}</li>
                </ul>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTemplateModal(false);
                    setEditingTemplate(null);
                    setTemplateForm({ templateName: '', description: '', isDefault: false });
                  }}
                  fullWidth
                >
                  Annulla
                </Button>
                <Button
                  variant="default"
                  iconName="Save"
                  onClick={handleSaveAsTemplate}
                  loading={loading}
                  fullWidth
                >
                  {editingTemplate ? 'Aggiorna' : 'Salva'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReports;
